import React, { useCallback, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";

import {
  listMessages,
  markConversationRead,
} from "../../whatsappApi";
import Composer from "./Composer";
import LinkPatientModal from "./LinkPatientModal";
import MessagePane from "./MessagePane";
import ThreadList from "./ThreadList";
import useWhatsAppSocket from "./useWhatsAppSocket";
import "../Accounts/CreateInvoice.css";
import "./whatsapp.css";

/**
 * InboxModal — the two-pane inbox.
 *
 * Why a modal rather than a dashboard pane: the dashboard gives WhatsApp one
 * column, and a thread list plus a message pane cannot share it at any useful
 * size. The dashboard hosts the thread list; the message pane opens here.
 *
 * Shape: a standard near-full-width modal (`modal-85w`, as Receipt and Credit
 * Note use) rather than a fullscreen one. It used to be `fullscreen="lg-down"`,
 * which made the inbox behave unlike every other modal in the app — edge to
 * edge with no backdrop on the sizes most people open it at.
 *
 * Conventions: /CLAUDE.md §1.8 (header tint, h6 title, subject suffix). No
 * Modal.Footer — the composer is the action surface and a footer would take
 * vertical space the message pane needs.
 */
export default function InboxModal({
  show,
  onHide,
  practiceNumber,
  practiceName,
  conversations,
  templates,
  loadingThreads,
  initialConversationId,
  onConversationsChanged,
  onViewPatient,
  showToast,
}) {
  const [activeId, setActiveId] = useState(initialConversationId || null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [showLink, setShowLink] = useState(false);
  // Mobile shows one pane at a time; desktop shows both.
  const [mobilePane, setMobilePane] = useState("threads");

  useEffect(() => {
    if (show) setActiveId(initialConversationId || null);
  }, [show, initialConversationId]);

  const active = useMemo(
    () => conversations.find((c) => String(c._id) === String(activeId)) || null,
    [conversations, activeId],
  );

  const loadMessages = useCallback(
    async (conversationId) => {
      if (!conversationId) return;
      setLoadingMessages(true);
      try {
        const data = await listMessages(practiceNumber, conversationId);
        setMessages(data.messages || []);
      } catch (err) {
        showToast?.(
          "danger",
          err?.response?.data?.error || "Could not load this conversation.",
        );
      } finally {
        setLoadingMessages(false);
      }
    },
    [practiceNumber, showToast],
  );

  useEffect(() => {
    if (!show || !activeId) return;
    loadMessages(activeId);
  }, [show, activeId, loadMessages]);

  const select = async (conversation) => {
    setActiveId(conversation._id);
    setMobilePane("messages");
    if (conversation.unreadCount > 0) {
      try {
        await markConversationRead(practiceNumber, conversation._id);
        onConversationsChanged?.();
      } catch (_err) {
        // A failed read-receipt is cosmetic; the thread still opens.
      }
    }
  };

  const filtered = useMemo(() => {
    let list = conversations;
    if (filter === "unread") list = list.filter((c) => c.unreadCount > 0);
    if (filter === "unlinked") list = list.filter((c) => !c.patientId);
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      list = list.filter((c) =>
        [c.patientName, c.profileName, c.displayPhone, c.waId]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(needle)),
      );
    }
    return list;
  }, [conversations, filter, search]);

  // Append without duplicating: the same message can arrive twice — once as the
  // send call's return value, once over the socket — and a thread that shows
  // every outbound line twice is worse than one that shows it late.
  const upsertMessage = useCallback((message) => {
    if (!message?._id) return;
    setMessages((prev) => {
      const id = String(message._id);
      const at = prev.findIndex((m) => String(m._id) === id);
      if (at === -1) return [...prev, message];
      const next = prev.slice();
      next[at] = { ...next[at], ...message };
      return next;
    });
  }, []);

  /**
   * Live updates for the thread that is open.
   *
   * InboxSection already listens for these, but only to refresh the thread
   * LIST — nothing was feeding the message pane, so anything that happened
   * while the modal was open (an inbound reply, a delivery receipt, and the
   * send that started it) only appeared after closing and reopening, which
   * remounts the modal and refetches.
   */
  useWhatsAppSocket({
    enabled: show && !!practiceNumber,
    handlers: {
      "whatsapp:message": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        upsertMessage(payload.message);
      },
      "whatsapp:status": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        // Keyed by waMessageId — a status report is all Meta gives us here.
        setMessages((prev) =>
          prev.map((m) =>
            m.waMessageId && m.waMessageId === payload.waMessageId
              ? { ...m, status: payload.status, errorMessage: payload.errorMessage }
              : m,
          ),
        );
      },
      "whatsapp:message-updated": (payload) => {
        if (String(payload.practiceNumber) !== String(practiceNumber)) return;
        if (String(payload.conversationId) !== String(activeId)) return;
        // Media finished downloading — refetch so the bubble gets its blob id.
        loadMessages(activeId);
      },
    },
  });

  const onSent = (message) => {
    upsertMessage(message);
    onConversationsChanged?.();
  };

  return (
    <>
      <Modal show={show} onHide={onHide} dialogClassName="modal-85w">
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            WhatsApp{practiceName ? ` — ${practiceName}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-0 wa-inbox-body">
          <Row className="g-0 h-100">
            <Col
              md={4}
              className={`wa-thread-col ${
                mobilePane === "messages" ? "d-none d-md-block" : ""
              }`}
            >
              <ThreadList
                conversations={filtered}
                activeId={activeId}
                onSelect={select}
                loading={loadingThreads}
                filter={filter}
                onFilterChange={setFilter}
                search={search}
                onSearchChange={setSearch}
              />
            </Col>

            <Col
              md={8}
              className={`wa-message-col ${
                mobilePane === "threads" ? "d-none d-md-flex" : "d-flex"
              }`}
            >
              <MessagePane
                practiceNumber={practiceNumber}
                conversation={active}
                messages={messages}
                loading={loadingMessages}
                onBack={() => setMobilePane("threads")}
                onViewPatient={onViewPatient}
              />
              <Composer
                practiceNumber={practiceNumber}
                conversation={active}
                templates={templates}
                onSent={onSent}
                onLinkPatient={() => setShowLink(true)}
                onConsentChanged={onConversationsChanged}
                showToast={showToast}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {active && (
        <LinkPatientModal
          show={showLink}
          onHide={() => setShowLink(false)}
          practiceNumber={practiceNumber}
          conversation={active}
          onLinked={onConversationsChanged}
          showToast={showToast}
        />
      )}
    </>
  );
}
